import {motion, useAnimation} from 'framer-motion'
import {useEffect, useRef} from 'react'

const LINES = [
  [
    {x: -20, y: 137},
    {x: 0, y: 134},
    {x: 120, y: 128},
    {x: 240, y: 124},
    {x: 328, y: 116},
    {x: 350, y: 114}
  ],
  [
    {x: -20, y: 134},
    {x: 0, y: 131},
    {x: 120, y: 121},
    {x: 240, y: 105},
    {x: 328, y: 88},
    {x: 350, y: 85}
  ],
  [
    {x: -20, y: 130},
    {x: 0, y: 127},
    {x: 119, y: 111},
    {x: 243, y: 87},
    {x: 328, y: 63},
    {x: 350, y: 58}
  ],
  [
    {x: -20, y: 127},
    {x: 0, y: 124},
    {x: 120, y: 96},
    {x: 243, y: 69},
    {x: 328, y: 43},
    {x: 350, y: 38}
  ],
  [
    {x: -20, y: 123},
    {x: 0, y: 120},
    {x: 91, y: 88},
    {x: 188, y: 60},
    {x: 328, y: 18},
    {x: 350, y: 13}
  ],
  [
    {x: -20, y: 119},
    {x: 0, y: 116},
    {x: 88, y: 77},
    {x: 193, y: 43},
    {x: 328, y: 6},
    {x: 350, y: 1}
  ]
]

const getDot = (points, index) => {
  const step = 1 / (points.length - 1)
  const t = index * step
  const i = Math.floor(t * (points.length - 1))
  const a = points[i]
  const b = points[i + 1] || a
  const localT = (t - i * step) / step
  return {
    x: a.x + (b.x - a.x) * localT,
    y: a.y + (b.y - a.y) * localT
  }
}

const getYAtX = (points, targetX) => {
  // Find the two points that bracket targetX
  for (let i = 0; i < points.length - 1; i++) {
    if (points[i].x <= targetX && points[i + 1].x >= targetX) {
      const p1 = points[i]
      const p2 = points[i + 1]
      const t = (targetX - p1.x) / (p2.x - p1.x)
      return p1.y + (p2.y - p1.y) * t
    }
  }
  return points[0].y // fallback
}

export function Graph({answer, index}) {
  const pathRef = useRef(null)
  const controls = useAnimation()
  const fillControls = useAnimation()
  const dotControls = useAnimation()

  const activeLine = LINES[index]
  const today = getDot(activeLine, 1) // Adjusted for new point array
  const mid = {x: 164, y: getYAtX(activeLine, 164)} // Fixed x position at center, Y on the line
  const end = getDot(activeLine, 4)

  useEffect(() => {
    controls.set({pathLength: 0})
    fillControls.set({opacity: 0})
    dotControls.set({scale: 0})

    requestAnimationFrame(() => {
      controls.start({pathLength: 1, transition: {duration: 1}})
      fillControls.start({opacity: 0.12, transition: {duration: 1}})
      dotControls.start({scale: 1, transition: {delay: 0.4, duration: 0.5}})
    })
  }, [index])

  return (
    <div className={'block text-start'} style={{padding: '16px 20px'}}>
      <strong style={{fontSize: 20, color: '#3ED06A'}}>{answer.header}</strong>
      <p className={'notice-2 small-sm'}>{answer.note}</p>

      <svg viewBox="-20 0 370 160" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* dashed lines */}
        {LINES.map((line, i) => (
          <path
            key={i}
            d={`M${line.map(p => `${p.x},${p.y}`).join(' L')}`}
            stroke="#333A49"
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray="1 8"
          />
        ))}

        {/* gradient fill under the active line */}
        <motion.path
          d={`M${activeLine.map(p => `${p.x},${p.y}`).join(' L')} L350,138 L-20,138 Z`}
          fill="url(#gradientFill)"
          animate={fillControls}
        />

        {/* animated bold line */}
        <motion.path
          ref={pathRef}
          d={`M${activeLine.map(p => `${p.x},${p.y}`).join(' L')}`}
          stroke="url(#gradientStroke)"
          strokeWidth="4"
          strokeLinecap="round"
          fill="none"
          initial={{pathLength: 0}}
          animate={controls}
        />

        {/* vertical lines */}
        {[today.x, mid.x, end.x].map((x, i) => (
          <line
            key={i}
            x1={x}
            x2={x}
            y1={0}
            y2={138}
            stroke="url(#vline)"
          />
        ))}

        {/* dots */}
        {[today, mid, end].map((dot, i) => (
          <motion.circle
            key={i}
            cx={dot.x}
            cy={dot.y}
            r={[4, 6, 8][i]}
            fill={['#F4FCF6', '#A3E9B8', '#4ED477'][i]}
            stroke="#10131A"
            strokeWidth="4"
            animate={dotControls}
          />
        ))}

        {/* labels */}
        {[
          {x: today.x, label: 'TODAY', color: '#fff'},
          {x: mid.x, label: '2 WEEKS', color: '#fff'},
          {x: end.x, label: '1 MONTH', color: '#3ED06A'}
        ].map(({x, label, color}, i) => (
          <text
            key={i}
            x={x}
            y={152}
            textAnchor="middle"
            fontSize={10}
            fill={color}
          >
            {label}
          </text>
        ))}

        <defs>
          <linearGradient id="gradientFill" x1="2" y1="127.245" x2="160.877" y2="-69.7709"
                          gradientUnits="userSpaceOnUse">
            <stop stopColor="white"/>
            <stop offset="1" stopColor="#3ED06A"/>
          </linearGradient>
          <linearGradient id="gradientStroke" x1="2" y1="127.245" x2="160.877" y2="-69.7709"
                          gradientUnits="userSpaceOnUse">
            <stop stopColor="white"/>
            <stop offset="1" stopColor="#3ED06A"/>
          </linearGradient>
          <linearGradient id="vline" x1="0" y1="0" x2="0" y2="138" gradientUnits="userSpaceOnUse">
            <stop stopColor="#333A49" stopOpacity="0"/>
            <stop offset="1" stopColor="#333A49"/>
          </linearGradient>
        </defs>
      </svg>
    </div>
  )
}